import { useState, useEffect, useRef } from 'react'
import { IconClapper, IconClock, IconCheck, IconPlay } from '../icons'

/**
 * Web-host "Ready Up!" screen — the browser counterpart of the tvOS
 * PartyReadyUpHost, shown while a web-hosted party is in `pre-start`.
 *
 * Players confirm on their phones (writing `players/{uid}.ready`); their chips
 * light up here in real time. A 30s countdown auto-starts the round and the host
 * can "Start Round" early — both, plus the all-confirmed case, funnel into a
 * single guarded `onStart` (the App.jsx onForceStart handler, which drops
 * un-ready players from picker/turn assignment).
 */
export default function PartyReadyUpHost({ party, onStart, onAbandon }) {
  const players    = party?.players ? Object.entries(party.players) : []
  const total      = players.length
  const readyCount = players.filter(([, p]) => p.ready === true).length
  const allReady   = total >= 2 && readyCount === total

  // Single-fire guard so the all-ready watcher, the timer, and the button can't
  // double-trigger the round start (App's onForceStart isn't itself idempotent).
  const firedRef = useRef(false)
  const fire = () => {
    if (firedRef.current || readyCount < 2) return
    firedRef.current = true
    onStart?.()
  }

  // All confirmed → start immediately (don't wait out the clock).
  useEffect(() => { if (allReady) fire() }, [allReady]) // eslint-disable-line react-hooks/exhaustive-deps

  // 30s countdown synced off the host's stamp → auto-start at 0.
  const startedMs = party?.preStartStartedAt?.toMillis?.()
    ?? (party?.preStartStartedAt?.seconds != null ? party.preStartStartedAt.seconds * 1000 : null)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(id)
  }, [])
  const remaining = startedMs != null ? Math.max(0, Math.ceil(30 - (now - startedMs) / 1000)) : 30
  useEffect(() => {
    if (remaining <= 0 && readyCount >= 2) fire()
  }, [remaining, readyCount]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="party-lobby party-ready-up party-ready-up--host">
      <div className="party-ready-up__chip" aria-hidden="true"><IconClapper size={30} /></div>
      <h1 className="party-ready-up__title">Ready Up!</h1>
      <p className="party-ready-up__sub">
        {allReady ? 'Everyone’s ready — roll camera' : 'Players, tap “Let’s Go” on your phone'}
      </p>

      <div className="party-ready-up__countdown">
        <IconClock size={34} aria-hidden="true" />
        <span>0:{String(remaining).padStart(2, '0')}</span>
      </div>
      <p className="party-ready-up__readycount">{readyCount}/{total} ready</p>

      <div className="party-player-grid party-ready-up__grid">
        {players.map(([uid, p]) => {
          const initial = (p.username || '?').charAt(0).toUpperCase()
          return (
            <div
              key={uid}
              className={`party-player-chip${p.ready ? ' party-player-chip--ready' : ''}`}
            >
              <div className="party-player-chip__avatar">
                {p.avatar
                  ? <img src={p.avatar} alt={p.username} />
                  : <span className="party-avatar__initial">{initial}</span>}
              </div>
              <span className="party-player-chip__name">
                <span>{p.username || 'Guest'}</span>
                {p.ready
                  ? <span className="party-ready-check" aria-label="Ready"><IconCheck size={12} /></span>
                  : <span className="party-ready-dot" aria-label="Waiting" />}
              </span>
            </div>
          )
        })}
      </div>

      <button
        className="btn-primary party-ready-up__go"
        onClick={fire}
        disabled={readyCount < 2}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <IconPlay size={20} aria-hidden="true" />
          {allReady ? 'Start Round' : `Start Round (${readyCount}/${total})`}
        </span>
      </button>

      {onAbandon && (
        <button className="btn-ghost" onClick={onAbandon} style={{ marginTop: 10, fontSize: '0.8rem' }}>
          Abandon Party
        </button>
      )}
    </div>
  )
}
